/**
 * ROIPaybackCard - Displays ROI analysis and payback period.
 *
 * When installationCostPln is not provided (roi is undefined), shows a prompt.
 * Otherwise shows payback years, NPV, 25-year savings, and IRR.
 */

import { Card, Text, Badge, Stack, Group, Progress, ThemeIcon } from '@mantine/core';
import { IconClock, IconTrendingUp, IconTrendingDown, IconInfoCircle } from '@tabler/icons-react';
import type { ProsumerROIResult } from '../../types';

export interface ROIPaybackCardProps {
  roi?: ProsumerROIResult;
  installationCostPln?: number;
  subsidyPln?: number;
}

function formatPln(amount: number): string {
  return new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 }).format(Math.round(amount));
}

function formatPaybackYears(years: number | null): string {
  if (years === null) return '—';
  if (years > 25) return '> 25 lat';
  return new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 1 }).format(years) + ' roku';
}

export function ROIPaybackCard({ roi }: ROIPaybackCardProps) {
  if (!roi) {
    return (
      <Card withBorder radius="md" p="lg">
        <Group gap="sm">
          <ThemeIcon color="gray" variant="light" size="lg" radius="xl">
            <IconInfoCircle size={20} />
          </ThemeIcon>
          <Text c="dimmed" size="sm">
            Podaj koszt instalacji, aby zobaczyć zwrot
          </Text>
        </Group>
      </Card>
    );
  }

  const npv = roi.netPresentValue25yr;
  const npvPositive = npv !== null && npv >= 0;
  const npvColor = npvPositive ? 'green' : 'red';

  const paybackProgress =
    roi.paybackYears !== null && roi.paybackYears <= 25
      ? (roi.paybackYears / 25) * 100
      : 100;

  return (
    <Card withBorder radius="md" p="lg">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <ThemeIcon color="blue" variant="light" size="lg" radius="xl">
              <IconClock size={20} />
            </ThemeIcon>
            <Text fw={600} size="lg">Zwrot z inwestycji</Text>
          </Group>
          {npv !== null && (
            <Badge
              color={npvColor}
              size="lg"
              variant="light"
              data-testid="npv-badge"
              leftSection={npvPositive ? <IconTrendingUp size={12} /> : <IconTrendingDown size={12} />}
            >
              {npvPositive ? '+' : ''}PLN {formatPln(npv)}
            </Badge>
          )}
        </Group>

        {!npvPositive && npv !== null && (
          <Text c="red" size="sm" fw={500}>
            Inwestycja nie zwraca się
          </Text>
        )}

        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Okres zwrotu</Text>
            <Text fw={600} data-testid="payback-years">
              {formatPaybackYears(roi.paybackYears)}
            </Text>
          </Group>

          {roi.paybackCalendarQuarter && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Kwartał zwrotu</Text>
              <Text fw={500} data-testid="payback-quarter">
                {roi.paybackCalendarQuarter}
              </Text>
            </Group>
          )}

          <Progress
            value={paybackProgress}
            color="blue"
            size="sm"
            radius="xl"
            aria-label="Payback progress"
          />
          <Text size="xs" c="dimmed" ta="right">z 25 lat</Text>
        </Stack>

        <Group justify="space-between">
          <Text size="sm" c="dimmed">Oszczędności łączne (25 lat)</Text>
          <Text fw={600} data-testid="total-savings-25yr">
            PLN {formatPln(roi.totalSavings25yr)}
          </Text>
        </Group>

        {roi.irr !== null && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">IRR</Text>
            <Text fw={500} data-testid="irr-value">
              {new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 1 }).format(roi.irr)}%
            </Text>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
